'use client';

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { CommentViewer } from 'lib/comments';
import { useTranslations } from 'next-intl';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Switch,
} from 'ui';
import { updateCommentProfile, useCommentMutation } from './comment-api';
import { CommentAvatar } from './comment-avatar';
import { COMMENT_INPUT } from './comment-editor';
import { CommentErrorMessage } from './comment-identity';
import { MyCommentsPanel } from './my-comments';

export function CommentAccount({
  postId,
  viewer,
  manage,
}: {
  postId: string;
  viewer: CommentViewer;
  manage: boolean;
}) {
  const t = useTranslations('Blog.Comments');
  const [dialog, setDialog] = React.useState<
    'profile' | 'settings' | 'comments'
  >();
  const mutation = useCommentMutation(postId, manage);
  const trigger = React.useRef<HTMLButtonElement>(null);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            ref={trigger}
            type="button"
            aria-label={t('AccountMenu')}
            title={viewer.name}
            className="inline-flex size-9 items-center justify-center rounded-full text-secondary hover:bg-secondary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
          >
            <CommentAvatar
              name={viewer.name}
              image={viewer.avatar}
              className="size-7"
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-max min-w-44 border-secondary bg-primary text-primary"
          onCloseAutoFocus={(event) => {
            if (dialog) event.preventDefault();
          }}
        >
          <DropdownMenuItem onSelect={() => setDialog('profile')}>
            {t('ProfileSettings')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setDialog('comments')}>
            {t('MyComments')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setDialog('settings')}>
            {t('Settings')}
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-secondary" />
          <DropdownMenuItem
            disabled={mutation.isPending}
            onSelect={() => {
              mutation.mutate({ action: 'logout' });
            }}
          >
            {t('SignOut')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog
        open={!!dialog}
        onOpenChange={(open) => {
          if (!open) setDialog(undefined);
        }}
      >
        <DialogContent
          closeLabel={t('Close')}
          className={`max-h-[90dvh] w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border-secondary bg-primary text-primary sm:w-[calc(100vw-2rem)] ${dialog === 'comments' ? 'max-w-2xl sm:max-w-2xl' : 'max-w-md sm:max-w-md'}`}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            trigger.current?.focus();
          }}
        >
          <DialogHeader className="mb-2 pe-6">
            <DialogTitle>
              {dialog === 'profile'
                ? t('ProfileSettings')
                : dialog === 'settings'
                  ? t('Settings')
                  : t('MyComments')}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {viewer.name}
            </DialogDescription>
          </DialogHeader>
          {dialog === 'profile' && (
            <CommentProfileForm
              key={viewer.name + (viewer.avatar || '')}
              viewer={viewer}
              postId={postId}
              manage={manage}
              close={() => setDialog(undefined)}
            />
          )}
          {dialog === 'settings' && (
            <CommentSettingsForm
              key={`${viewer.replyNotifications}-${viewer.likeNotifications}`}
              viewer={viewer}
              postId={postId}
              manage={manage}
              close={() => setDialog(undefined)}
            />
          )}
          {dialog === 'comments' && (
            <MyCommentsPanel
              postId={postId}
              manage={manage}
              identity={viewer.email}
            />
          )}
        </DialogContent>
      </Dialog>
      {mutation.error && <CommentErrorMessage code={mutation.error.message} />}
    </>
  );
}

function CommentProfileForm({
  viewer,
  postId,
  manage,
  close,
}: {
  viewer: CommentViewer;
  postId: string;
  manage: boolean;
  close: () => void;
}) {
  const t = useTranslations('Blog.Comments');
  const mutation = useCommentMutation(postId, manage);
  const client = useQueryClient();
  const [file, setFile] = React.useState<File>();
  const [preview, setPreview] = React.useState(viewer.avatar);
  const [removeAvatar, setRemoveAvatar] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const fileInput = React.useRef<HTMLInputElement>(null);
  const nameId = React.useId();
  const emailId = React.useId();
  const busy = mutation.isPending || uploading;
  React.useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <form
      className="space-y-5"
      onSubmit={async (event) => {
        event.preventDefault();
        setError(undefined);
        const name = String(
          new FormData(event.currentTarget).get('name') || '',
        );
        try {
          if (file) {
            setUploading(true);
            const data = new FormData();
            data.set('file', file);
            data.set('name', name);
            const response = await fetch(
              `/.quotion/api/comments?postId=${encodeURIComponent(postId)}&manage=${manage}`,
              { method: 'POST', credentials: 'same-origin', body: data },
            );
            const result = (await response.json()) as {
              error?: string;
              avatar?: string;
            };
            if (!response.ok) throw new Error(result.error || 'request-failed');
            updateCommentProfile(client, postId, {
              name,
              avatar: result.avatar,
            });
          } else
            await mutation.mutateAsync({
              action: 'profile',
              name,
              removeAvatar: !file && removeAvatar,
            });
          void client.invalidateQueries({ queryKey: ['comments'] });
          close();
        } catch (error) {
          setError(error instanceof Error ? error.message : 'request-failed');
        } finally {
          setUploading(false);
        }
      }}
    >
      <div className="flex items-center gap-4">
        <CommentAvatar name={viewer.name} image={preview} className="size-16" />
        <div className="space-y-1">
          <div className="flex gap-3 text-sm">
            <button
              type="button"
              disabled={busy}
              className="rounded font-medium text-secondary hover:text-primary"
              onClick={() => fileInput.current?.click()}
            >
              {t('ChangeAvatar')}
            </button>
            {preview && (
              <button
                type="button"
                disabled={busy}
                className="rounded text-tertiary hover:text-primary"
                onClick={() => {
                  setFile(undefined);
                  setPreview(null);
                  setRemoveAvatar(true);
                  if (fileInput.current) fileInput.current.value = '';
                }}
              >
                {t('Remove')}
              </button>
            )}
          </div>
          <p className="text-xs text-tertiary">{t('AvatarHint')}</p>
        </div>
        <input
          ref={fileInput}
          aria-label={t('ChangeAvatar')}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="sr-only"
          disabled={busy}
          onChange={(event) => {
            const selected = event.target.files?.[0];
            if (!selected) return;
            if (selected.size > 5 * 1024 * 1024) {
              setError('avatar-too-large');
              return;
            }
            setError(undefined);
            setFile(selected);
            setRemoveAvatar(false);
          }}
        />
      </div>
      <div className="space-y-2">
        <label htmlFor={nameId} className="text-sm text-secondary">
          {t('Nickname')}
        </label>
        <input
          id={nameId}
          className={COMMENT_INPUT}
          name="name"
          required
          minLength={2}
          maxLength={36}
          defaultValue={viewer.name}
          autoComplete="nickname"
          disabled={busy}
        />
      </div>
      <div className="space-y-2">
        <label htmlFor={emailId} className="text-sm text-secondary">
          {t('Email')}
        </label>
        <input
          id={emailId}
          className={`${COMMENT_INPUT} text-tertiary`}
          value={viewer.email}
          readOnly
          aria-describedby={`${emailId}-privacy`}
        />
        <p id={`${emailId}-privacy`} className="text-xs text-tertiary">
          {t('EmailPrivate')}
        </p>
      </div>
      {(error || mutation.error) && (
        <CommentErrorMessage code={error || mutation.error!.message} />
      )}
      <Button type="submit" className="h-10 w-full" disabled={busy}>
        {busy ? t('Saving') : t('Save')}
      </Button>
    </form>
  );
}

function CommentSettingsForm({
  viewer,
  postId,
  manage,
  close,
}: {
  viewer: CommentViewer;
  postId: string;
  manage: boolean;
  close: () => void;
}) {
  const t = useTranslations('Blog.Comments');
  const mutation = useCommentMutation(postId, manage);
  const [replies, setReplies] = React.useState(viewer.replyNotifications);
  const [likes, setLikes] = React.useState(viewer.likeNotifications);
  const repliesId = React.useId();
  const likesId = React.useId();
  return (
    <form
      className="space-y-6"
      onSubmit={async (event) => {
        event.preventDefault();
        try {
          await mutation.mutateAsync({
            action: 'settings',
            replyNotifications: replies,
            likeNotifications: likes,
          });
          close();
        } catch {
          /* Keep the settings open with the server error. */
        }
      }}
    >
      <div className="divide-y divide-secondary">
        <div className="flex items-center justify-between gap-4 py-3">
          <label htmlFor={repliesId} className="text-sm text-secondary">
            {t('ReplyNotifications')}
          </label>
          <Switch
            id={repliesId}
            size="sm"
            checked={replies}
            onCheckedChange={setReplies}
            disabled={mutation.isPending}
          />
        </div>
        <div className="flex items-center justify-between gap-4 py-3">
          <label htmlFor={likesId} className="text-sm text-secondary">
            {t('LikeNotifications')}
          </label>
          <Switch
            id={likesId}
            size="sm"
            checked={likes}
            onCheckedChange={setLikes}
            disabled={mutation.isPending}
          />
        </div>
      </div>
      {mutation.error && <CommentErrorMessage code={mutation.error.message} />}
      <Button
        type="submit"
        className="h-10 w-full"
        disabled={mutation.isPending}
      >
        {mutation.isPending ? t('Saving') : t('Save')}
      </Button>
    </form>
  );
}
